/**
 * @file stock-placement-texts.ts
 * @description Тексты размещения из журнала остатков (phase19.24 этап 1,
 * шаг 4).
 *
 * Все фразы экрана — один реестр: подписи пунктов «⋯», заголовки шторки,
 * тосты и причины блокировки кнопки. Без него формулировка «Разместить» жила
 * бы в четырёх файлах и разъехалась после первой же правки (правило
 * docs/processes/engineering-principles.md — реестры вместо if-каскадов).
 *
 * Разбор ответа сервера живёт рядом: пропущенные партии — здесь (текст полосы
 * собирается из них же), ошибки проведения — в `stock-placement-error.ts`.
 */

import type { PlacementSkippedItem } from "../../../lib/api-transfers";

/** Тексты экрана размещения. Функции — там, где в строку подставляются данные. */
export const PLACEMENT_TEXT = {
  // ─── Меню «⋯» строки остатка ───────────────────────────────────────────────
  rowMenuTitle: "Действия с остатком",
  actionPlace: "Разместить в ячейку",
  actionMove: "Переставить",
  actionLotMovements: "Движения по партии",
  actionSkuMovements: "Движения по товару",
  actionProduct: "Открыть товар",

  // ─── Отбор и панель выбора ─────────────────────────────────────────────────
  selectRowLabel: "Выбрать партию для размещения",
  selectedCount: (count: number) => `Выбрано ${count}`,
  placeSelected: "Разместить выбранные",
  clearSelection: "Снять выбор",
  noLotHint: "Без партии — адрес хранения задать нельзя",

  // ─── Шторка размещения ─────────────────────────────────────────────────────
  sheetTitle: "Разместить партии",
  sheetDescription:
    "Один документ перемещения проводится сразу: остаток и склад не меняются, партии получают адрес.",
  modeShared: "Одна ячейка для всех",
  modePerRow: "По строкам",
  sharedBinLabel: "Ячейка для всех партий",
  rowBinLabel: "Ячейка",
  scanLabel: "Скан ячейки",
  scanHint: "Отсканируйте код места (LOC:A-01-01) или выберите ячейку в списке",
  submit: "Провести размещение",
  submitting: "Проводим…",
  cancel: "Отмена",
  emptySelection: "Выберите партии в списке остатков",
  noBins: "На этом складе ячеек нет. Заведите их в разделе «Ячейки».",
  binsLoadError: "Не удалось загрузить ячейки",
  mixedWarehouses:
    "В отборе партии с разных складов — размещение проводится по одному складу. Выберите склад в фильтрах.",
  hintNeedSharedBin: "Выберите ячейку, куда положить все партии",
  hintNeedRowBins: (count: number) =>
    count === 1 ? "У одной партии не выбрана ячейка" : `У ${count} партий не выбрана ячейка`,

  // ─── Результат ─────────────────────────────────────────────────────────────
  placedOne: (batchNumber: string, binCode: string) =>
    `Партия ${batchNumber} размещена в ${binCode}`,
  placedMany: (count: number, number: string) =>
    `Размещено партий: ${count}. Документ ${number}`,
  // Пропуск — не ошибка: партия уже лежит там, куда её просили положить.
  skippedSome: (count: number) =>
    `Пропущено партий: ${count} — уже лежат в выбранной ячейке`,
  skippedAll: "Все выбранные партии уже в этой ячейке — размещать нечего",
  openDocument: "Открыть документ",
  placementError: "Не удалось провести размещение",
  /**
   * Проведение упало, но документ сервер сохранил черновиком: части партий
   * адрес мог уже достаться. Молчать об этом нельзя — незакрытый черновик MV
   * висит в журнале, и разобрать его можно только руками.
   */
  placementPartial: (transferNumber: string) =>
    `Размещение не проведено до конца — документ ${transferNumber} сохранён черновиком. ` +
    "Откройте его и нажмите «Обновить строки».",
  /** То же, когда сервер назвал документ только идентификатором. */
  placementPartialNoNumber:
    "Размещение не проведено до конца — документ сохранён черновиком. " +
    "Откройте его и нажмите «Обновить строки».",
  binNotFound: (code: string) => `Ячейки ${code} на этом складе нет`,
} as const;

/**
 * Полоса о пропущенных партиях: что сказать и каким тоном.
 *
 * Документа нет вовсе (`transfer = null`) — это не успех и не ошибка, а
 * «делать было нечего», поэтому фраза другая и ссылки на документ в тосте не
 * будет. Партии перечисляем номером и адресом: кладовщик идёт проверять полку.
 */
export function placementSkippedNotice(
  skipped: PlacementSkippedItem[],
  hasDocument: boolean,
): string | null {
  if (skipped.length === 0) return null;
  const list = skipped
    .map((item) => `${item.batchNumber} · ${item.binCode}`)
    .join(", ");
  const head = hasDocument
    ? PLACEMENT_TEXT.skippedSome(skipped.length)
    : PLACEMENT_TEXT.skippedAll;
  return `${head}: ${list}`;
}

